import { Num } from "@/components/Num";
import { Link } from "react-router-dom";
import { ArrowUpRight, Trash2, CalendarDays } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { APPROVAL_STYLE } from "@/components/ApprovalBar";
import { fmtPct, MONTHS } from "@/lib/format";

const STATUS = {
  new: { label: "No data yet", cls: "bg-slate-100 text-slate-600" },
  uploaded: { label: "Data ready", cls: "bg-amber-100 text-amber-800" },
  generated: { label: "Budget generated", cls: "bg-emerald-100 text-emerald-800" },
};

export const ProjectCard = ({ project, onDelete, index = 0 }) => {
  const st = STATUS[project.status] || STATUS.new;
  const ap = project.approval?.status && project.approval.status !== "draft" ? APPROVAL_STYLE[project.approval.status] : null;
  const rev = project.kpis?.revenue;
  const gop = project.kpis?.gop;
  return (
    <div className={`group relative bg-white border border-slate-200 rounded-lg p-5 sm:p-6 hover:border-emerald-400 hover:shadow-[0_8px_30px_-12px_rgba(5,150,105,0.35)] transition-all duration-300 rise rise-${Math.min(index % 4 + 1, 4)}`} data-testid={`project-card-${project.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold truncate">{project.hotel_name}</p>
          <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mt-1 leading-tight">{project.name}</h3>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Badge className={`${st.cls} border-0`} data-testid="project-status-badge">{st.label}</Badge>
          {ap && <Badge className={`${ap.cls} border-0`} data-testid="project-approval-badge">{ap.label}</Badge>}
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-xs text-slate-500">Budget Year</p>
          <p className="font-mono text-slate-900 font-semibold">{project.budget_year}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Actuals through</p>
          <p className="font-mono text-slate-900 font-semibold">{project.last_actual_month ? `${MONTHS[project.last_actual_month - 1]} ${project.actual_year || project.budget_year - 1}` : "-"}</p>
        </div>
        {rev && (
          <div>
            <p className="text-xs text-slate-500">Budget Revenue</p>
            <p className="font-mono text-slate-900 font-semibold"><Num v={rev.budget_total} compact /> <span className={`text-xs ${rev.var_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtPct(rev.var_pct)}</span></p>
          </div>
        )}
        {gop && (
          <div>
            <p className="text-xs text-slate-500">Budget GOP</p>
            <p className="font-mono text-slate-900 font-semibold"><Num v={gop.budget_total} compact /> <span className={`text-xs ${gop.var_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtPct(gop.var_pct)}</span></p>
          </div>
        )}
      </div>
      <div className="mt-6 flex items-center justify-between">
        <span className="text-xs text-slate-400 inline-flex items-center gap-1"><CalendarDays size={12} /> {new Date(project.updated_at).toLocaleDateString("en-GB")}</span>
        <div className="flex items-center gap-2">
          {onDelete && <button onClick={() => onDelete(project)} className="text-slate-400 hover:text-red-600 transition-colors p-1" data-testid={`delete-project-${project.id}`} title="Delete"><Trash2 size={15} /></button>}
          <Link to={`/projects/${project.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900 group-hover:text-emerald-700 transition-colors" data-testid={`open-project-${project.id}`}>
            Open <ArrowUpRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
};
